import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { toast } from 'sonner';
import {
  ShopifyProduct,
  CART_QUERY,
  storefrontApiRequest,
  createShopifyCart,
  addLineToShopifyCart,
  updateShopifyCartLine,
  removeLineFromShopifyCart,
} from '@/lib/shopify';

export interface CartItem {
  lineId: string | null;
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: { amount: string; currencyCode: string };
  quantity: number;
  selectedOptions: Array<{ name: string; value: string }>;
}

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  addItem: (item: Omit<CartItem, 'lineId'>) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
  getCheckoutUrl: () => string | null;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isSyncing: false,

      addItem: async (item) => {
        const { items, cartId, clearCart } = get();
        const existingItem = items.find(i => i.variantId === item.variantId);

        set({ isLoading: true });
        try {
          if (!cartId) {
            const result = await createShopifyCart(item.variantId, item.quantity);
            if (result) {
              set({
                cartId: result.cartId,
                checkoutUrl: result.checkoutUrl,
                items: [{ ...item, lineId: result.lineId }],
              });
            }
          } else if (existingItem) {
            const newQty = existingItem.quantity + item.quantity;
            if (!existingItem.lineId) return;
            // Optimistic update
            set({ items: items.map(i => i.variantId === item.variantId ? { ...i, quantity: newQty } : i) });
            const result = await updateShopifyCartLine(cartId, existingItem.lineId, newQty);
            if (result.cartNotFound) {
              clearCart();
            } else if (!result.success) {
              // Rollback
              set({ items });
              toast.error("Failed to update cart. Please try again.");
            }
          } else {
            const result = await addLineToShopifyCart(cartId, item.variantId, item.quantity);
            if (result.success) {
              set({ items: [...get().items, { ...item, lineId: result.lineId ?? null }] });
            } else if (result.cartNotFound) {
              clearCart();
            } else {
              toast.error("Failed to add item. Please try again.");
            }
          }
        } catch (error) {
          console.error('Failed to add item:', error);
          toast.error("Something went wrong. Please try again.");
        } finally {
          set({ isLoading: false });
        }
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) {
          await get().removeItem(variantId);
          return;
        }
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;

        const prevQty = item.quantity;
        // Optimistic update
        set({ isLoading: true, items: items.map(i => i.variantId === variantId ? { ...i, quantity } : i) });
        try {
          const result = await updateShopifyCartLine(cartId, item.lineId, quantity);
          if (result.cartNotFound) {
            clearCart();
          } else if (!result.success) {
            // Rollback
            set({ items: get().items.map(i => i.variantId === variantId ? { ...i, quantity: prevQty } : i) });
            toast.error("Failed to update quantity. Please try again.");
          }
        } catch (error) {
          set({ items: get().items.map(i => i.variantId === variantId ? { ...i, quantity: prevQty } : i) });
          toast.error("Something went wrong. Please try again.");
        } finally {
          set({ isLoading: false });
        }
      },

      removeItem: async (variantId) => {
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;

        // Optimistic remove
        const prevItems = items;
        const newItems = items.filter(i => i.variantId !== variantId);
        set({ isLoading: true, items: newItems.length ? newItems : [] });

        try {
          const result = await removeLineFromShopifyCart(cartId, item.lineId);
          if (result.success) {
            if (!newItems.length) clearCart();
          } else if (result.cartNotFound) {
            clearCart();
          } else {
            // Rollback
            set({ items: prevItems });
            toast.error("Failed to remove item. Please try again.");
          }
        } catch (error) {
          set({ items: prevItems });
          toast.error("Something went wrong. Please try again.");
        } finally {
          set({ isLoading: false });
        }
      },

      clearCart: () => set({ items: [], cartId: null, checkoutUrl: null }),
      getCheckoutUrl: () => get().checkoutUrl,

      syncCart: async () => {
        const { cartId, isSyncing, clearCart } = get();
        if (!cartId || isSyncing) return;

        set({ isSyncing: true });
        try {
          const data = await storefrontApiRequest(CART_QUERY, { id: cartId });
          if (!data) return;
          const cart = data?.data?.cart;
          if (!cart || cart.totalQuantity === 0) clearCart();
        } catch (error) {
          console.error('Failed to sync cart:', error);
          // Silent — retry will happen on next visibility change
        } finally {
          set({ isSyncing: false });
        }
      },
    }),
    {
      name: 'shopify-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items, cartId: state.cartId, checkoutUrl: state.checkoutUrl }),
    }
  )
);
